import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "./AuthContext";
import { Zap, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_house-rewire/artifacts/7meozpw2_LOGO%20-%20NO%20BACKGROUND.png";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (user) {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  const from = location.state?.from?.pathname || "/admin";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      toast.error("Sign in failed", {
        description: formatApiErrorDetail(err.response?.data?.detail),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="LK Electrical & Security" className="h-14 w-auto mx-auto mb-4" data-testid="login-logo" />
          <div className="inline-flex items-center gap-2 bg-[#F5C518]/10 text-[#F5C518] text-xs font-semibold px-3 py-1.5 rounded-full border border-[#F5C518]/20">
            <Zap className="w-3.5 h-3.5" />
            LK Lead Command
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-[#18181b] p-8 rounded-2xl border border-[#27272a] shadow-2xl"
          data-testid="login-form"
        >
          <h1 className="text-2xl font-bold text-white mb-1">Sign in</h1>
          <p className="text-gray-400 text-sm mb-6">Access your lead dashboard</p>

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">Email</label>
              <input
                type="email"
                id="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@lkelectricalandsecurity.ie"
                className="bg-[#27272a] border border-[#3f3f46] text-white rounded-lg p-3 w-full focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none transition-all"
                data-testid="login-email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  className="bg-[#27272a] border border-[#3f3f46] text-white rounded-lg p-3 w-full pr-11 focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none transition-all"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  data-testid="toggle-password-visibility"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#F5C518] hover:bg-[#FFD54F] text-black font-bold py-3.5 px-8 rounded-lg transition-all duration-300 shadow-[0_0_20px_rgba(245,197,24,0.3)] hover:shadow-[0_0_30px_rgba(245,197,24,0.5)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
